import React, { useEffect, useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

export default function AdminEmails() {
  const { t } = useLang();
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [active, setActive] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = () => Promise.all([api.get("/email-templates"), api.get("/email-logs")]).then(([a, b]) => { setTemplates(a.data); setLogs(b.data); if (!active && a.data[0]) { setActive(a.data[0]); setEdit(a.data[0]); } });
  useEffect(() => { load(); }, []);

  useEffect(() => { if (active) setEdit({ ...active }); }, [active]);

  const save = async () => {
    try { await api.put(`/email-templates/${edit.key}`, edit); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-emails">
      <p className="overline mb-1">OFA France · Admin</p>
      <h1 className="font-serif text-3xl tracking-tight mb-6">{t("admin.emails")}</h1>

      <Tabs defaultValue="templates">
        <TabsList>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Logs ({logs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="templates">
          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-1">
              <CardContent className="p-2">
                {templates.map(tpl => (
                  <button key={tpl.key} data-testid={`tpl-${tpl.key}`} onClick={() => setActive(tpl)} className={`w-full text-left p-3 rounded-md text-sm transition-colors ${active?.key === tpl.key ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                    <div className="font-medium">{tpl.name_fr}</div>
                    <div className="text-xs opacity-70">{tpl.name_en}</div>
                  </button>
                ))}
              </CardContent>
            </Card>
            <Card className="lg:col-span-2">
              {edit && (
                <CardContent className="p-6 space-y-4">
                  <div><Label>Key</Label><Input value={edit.key} disabled /></div>
                  <Tabs defaultValue="fr">
                    <TabsList><TabsTrigger value="fr" data-testid="tpl-tab-fr">Français</TabsTrigger><TabsTrigger value="en" data-testid="tpl-tab-en">English</TabsTrigger></TabsList>
                    <TabsContent value="fr" className="space-y-3 pt-3">
                      <div><Label>Subject FR</Label><Input data-testid="subj-fr" value={edit.subject_fr} onChange={(e) => setEdit({ ...edit, subject_fr: e.target.value })} /></div>
                      <div><Label>Body FR</Label><Textarea data-testid="body-fr" rows={10} value={edit.body_fr} onChange={(e) => setEdit({ ...edit, body_fr: e.target.value })} /></div>
                    </TabsContent>
                    <TabsContent value="en" className="space-y-3 pt-3">
                      <div><Label>Subject EN</Label><Input data-testid="subj-en" value={edit.subject_en} onChange={(e) => setEdit({ ...edit, subject_en: e.target.value })} /></div>
                      <div><Label>Body EN</Label><Textarea data-testid="body-en" rows={10} value={edit.body_en} onChange={(e) => setEdit({ ...edit, body_en: e.target.value })} /></div>
                    </TabsContent>
                  </Tabs>
                  <Button data-testid="tpl-save" onClick={save} className="bg-accent text-accent-foreground hover:bg-accent/90">{t("common.save")}</Button>
                </CardContent>
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="logs">
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="p-3 text-left">When</th><th className="p-3 text-left">Template</th><th className="p-3 text-left">To</th><th className="p-3 text-left">Lang</th><th className="p-3 text-left">Subject</th><th className="p-3 text-left">Status</th></tr>
                </thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id} data-testid={`log-${l.id}`} className="border-t border-border">
                      <td className="p-3 text-xs">{new Date(l.sent_at).toLocaleString()}</td>
                      <td className="p-3 text-xs">{l.template_key}</td>
                      <td className="p-3 text-xs">{l.to}</td>
                      <td className="p-3 text-xs uppercase">{l.language}</td>
                      <td className="p-3 text-xs">{l.subject}</td>
                      <td className="p-3"><Badge variant="outline">{t(`statuses.${l.status}`)}</Badge></td>
                    </tr>
                  ))}
                  {logs.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-muted-foreground">No logs yet</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
